// src/components/admin/NewTableDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import * as tablesApi from '../../services/tablesApi';

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const NewTableDialog = ({
  open,
  restaurantId = '1',
  onClose,
  onCreated, // (createdRow) => void
  fetchEmployees, // optional override; defaults to tablesApi.listEmployees
  tenantServerUrl
}) => {
  const [number, setNumber]       = useState('');
  const [nickname, setNickname]   = useState('');
  const [capacity, setCapacity]   = useState('4');
  const [enabled, setEnabled]     = useState(true); // Show in App
  const [waiter, setWaiter]       = useState('');

  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');

  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empErr, setEmpErr] = useState('');

  // Load employees for the waiter dropdown
  useEffect(() => {
    if (!open) return;
    if (!tenantServerUrl && typeof fetchEmployees !== 'function') return;
    let alive = true;
    (async () => {
      try {
        setEmpLoading(true);
        setEmpErr('');
        let raw = [];
        if (typeof fetchEmployees === 'function') {
          raw = await fetchEmployees({ restaurantId });
        } else if (tablesApi && typeof tablesApi.listEmployees === 'function') {
          raw = await tablesApi.listEmployees(tenantServerUrl, { restaurantId });
        }
        const normalized = (raw || []).map(e => {
          const id = e?.username ?? e?.email ?? e?.id ?? e?.employee_id ?? e?.user_id ?? '';
          const display = e?.display_name ?? e?.name ?? e?.fullname ?? id;
          const label = id && display && id !== display ? `${display} (${id})` : (display || id);
          return { value: String(id || ''), label };
        }).filter(x => x.value);
        if (alive) setEmployees(normalized);
      } catch (e) {
        console.error('[NewTableDialog] employees fetch failed:', e);
        if (alive) setEmpErr('Failed to load employees');
      } finally {
        if (alive) setEmpLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, restaurantId, fetchEmployees, tenantServerUrl]);

  // Reset when opening
  useEffect(() => {
    if (!open) return;
    setErr('');
    setBusy(false);
    setNumber('');
    setNickname('');
    setCapacity('4');
    setEnabled(true);
    setWaiter('');
  }, [open]);

  const input = {
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    background: '#fff',
  };
  const btn = {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #dfe3e6',
    background: '#fff',
    cursor: 'pointer',
  };
  const btnPrimary = {
    ...btn,
    border: '1px solid #0a7',
    background: '#0c8',
    color: '#fff',
  };

  const validate = () => {
    const n = toInt(number, NaN);
    const c = toInt(capacity, NaN);
    if (!Number.isFinite(n) || n <= 0) return 'Table number must be a positive integer.';
    if (!nickname.trim()) return 'Nickname is required.';
    if (!Number.isFinite(c) || c <= 0) return 'Capacity must be a positive integer.';
    return '';
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const v = validate();
    if (v) { setErr(v); return; }

    const payload = {
      // use both names to be safe for your API
      number: toInt(number),
      nickname: nickname.trim(),
      capacity: toInt(capacity),
      enabled: enabled ? 1 : 0,       // Show in app
      visibility: enabled ? 1 : 0,    // (if your PHP expects `visibility`)
      default_waiter: waiter || '',   // preferred field
      waiter: waiter || '',           // fallback if your PHP expects `waiter`
      restaurant_id: restaurantId,
      bywho: (localStorage.getItem('loggedUser_Username')
           || localStorage.getItem('loggedUser_Email')
           || 'admin'),
    };

    try {
      setBusy(true);
      setErr('');
      console.log('[NewTableDialog] create payload:', payload);

      // Your real API call
      const created = await tablesApi.addTable(tenantServerUrl,payload);

      console.log('[NewTableDialog] create result:', created);
      onCreated?.(created);
      onClose?.();
    } catch (e) {
      console.error('[NewTableDialog] create failed:', e);
      setErr(e?.message || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="tm-new-table-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        padding: 12
      }}
      onClick={onClose}
    >
      <div
        className="tm-new-table-dialog"
        style={{
          width: 520, maxWidth: '92vw', background: '#fff', borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)', padding: 16
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>Add New Table</div>

        <form onSubmit={submit}>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#555' }}>Number *</span>
              <input
                inputMode="numeric"
                value={number}
                onChange={(e) => setNumber(e.target.value.replace(/[^\d]/g, ''))}
                style={input}
                placeholder="e.g., 12"
                autoFocus
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#555' }}>Capacity *</span>
              <input
                inputMode="numeric"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value.replace(/[^\d]/g, ''))}
                style={input}
                placeholder="e.g., 4"
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#555' }}>Nickname *</span>
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                style={input}
                placeholder="e.g., Window Booth"
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: '#555' }}>Default Waiter</span>
              <select
                value={waiter}
                onChange={(e) => setWaiter(e.target.value)}
                disabled={empLoading}
                style={input}
              >
                <option value="">{empLoading ? 'Loading…' : 'Select a waiter'}</option>
                {empErr && <option value="" disabled>Failed to load employees</option>}
                {employees.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px', margin: '12px 0',
            border: '1px solid #eee', borderRadius: 10, background: '#fafafa'
          }}>
            <input
              id="nt-enabled"
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <label htmlFor="nt-enabled" style={{ userSelect: 'none' }}>
              Show in App (Enable/Disable)
            </label>
          </div>

          {!!err && <div style={{ color: '#b3261e', fontSize: 12, marginBottom: 8 }}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy} style={btn}>
              Cancel
            </button>
            <button type="submit" disabled={busy} style={btnPrimary}>
              {busy ? 'Creating…' : 'Create Table'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTableDialog;
